import type { QuestionSet } from '@/api/questionSets';

/**
 * §6.5's **pre-filled set** — the client-side default the user's decision 1 asks for, and the whole of
 * it (2026-08-12).
 *
 * ⚠️ **There is no server concept behind this and `CONTEXT.md` says so in as many words.** No column,
 * no migration, no payload field and no route answers *"which set is this person's"*; what exists is
 * `GET /api/v1/question-sets/?author_id=`, which is an ordinary list narrowed by a filter (ADR 0016
 * decision 5(b)). So the rule below is **the screen's**, it is written down here rather than inside a
 * component, and it is a pure function so it can be checked without a render.
 *
 * ⚠️ **The rule, deterministic and stated — three cases and no fourth:**
 *
 *  1. **The sets the chosen interviewer wrote.** Among several, the **lowest id**, which is the same
 *     tie-break `services._set_for` uses among a vacancy's attached sets — one rule in the product
 *     rather than two, so *"why that one?"* has one answer wherever it is asked. Lowest id is *"the
 *     one they made first"*, which is the only ordering a set list carries.
 *  2. **Otherwise the vacancy's attached sets**, again the lowest id — and that is deliberately
 *     **byte-identical to what the server would do with no `question_set_id` at all**. The client
 *     names it anyway, because a field that shows nothing while the server silently picks something is
 *     a screen hiding a decision from the person taking it.
 *  3. **Otherwise no set**, which is a valid interview with zero scores (backend TASK-028) and the
 *     state §6.6's empty screen is the other half of.
 *
 * ⚠️ **Retired sets are excluded here as well as by the request.** `?include_retired=` defaults to
 * false, so the authored list has none — but a vacancy's attached list **does** return them (retiring
 * is not detaching), and naming a retired set at scheduling is a **404**. A pre-fill that could produce
 * one would be a dialog that refuses itself.
 */
export function preFilledSet(
  /** The active sets the **chosen interviewer** authored, as `?author_id=` answered them. */
  authored: QuestionSet[],
  /** The sets attached to this vacancy, retired ones included — filtered here. */
  vacancySets: QuestionSet[],
): number | null {
  const lowest = (sets: QuestionSet[]): number | null =>
    sets
      .filter((set) => set.is_active)
      .map((set) => set.id)
      .sort((a, b) => a - b)[0] ?? null;

  return lowest(authored) ?? lowest(vacancySets);
}

/**
 * The order the picker offers sets in — **the vacancy's first, then everything else this reader may
 * see** (ADR 0018 decision 3: the attachment decides what is shown *first* and nothing more).
 *
 * ⚠️ **Every set the reader may see is offered, attached or not, and that is the decision rather than
 * a convenience.** The server refuses **no** set on the grounds that it is not attached to the vacancy
 * — `test_a_set_attached_to_no_vacancy_and_one_attached_elsewhere_both_schedule` is the test that
 * decision exists for — so a picker that hid the unattached ones would be this client inventing the
 * funnel rule the product has already refused once, and inventing it where nothing could see it.
 *
 * Retired sets are dropped: naming one is a 404, so offering one would be offering a refusal.
 */
export function orderedSetOptions(
  vacancySets: QuestionSet[],
  tenantSets: QuestionSet[],
): Array<{ set: QuestionSet; offeredByThisVacancy: boolean }> {
  const attachedIds = new Set(vacancySets.filter((set) => set.is_active).map((set) => set.id));

  const offered = vacancySets.filter((set) => set.is_active);
  const rest = tenantSets.filter((set) => set.is_active && !attachedIds.has(set.id));

  return [
    ...offered.map((set) => ({ set, offeredByThisVacancy: true })),
    ...rest.map((set) => ({ set, offeredByThisVacancy: false })),
  ];
}

/** The sentinel the picker uses for *no set at all* — see `ScheduleInterviewDialog` for why it exists. */
export const NO_SET = 'none';
